// Static-page markdown renderer. Pages live in src/content/legal/*.md
// and are bundled at build time via Vite's `?raw` import suffix — no
// runtime fetch, no risk of a 404 in prod, and the SPA can deploy to
// a CDN without an API round-trip on every render.
//
// Content is authored by us, so we don't run an HTML sanitizer. If
// we ever take user-submitted markdown, switch to DOMPurify before
// returning the parsed string.

import { marked, type MarkedOptions } from 'marked';

import privacy from '../content/legal/privacy.md?raw';
import terms from '../content/legal/terms.md?raw';
import contact from '../content/legal/contact.md?raw';

const SOURCES: Record<string, string> = {
  privacy,
  terms,
  contact,
};

const MARKED_OPTIONS: MarkedOptions = {
  gfm: true,
  breaks: false,
};

export interface ParsedMarkdown {
  /** Page title pulled from the leading `# ...` line, if present. */
  title: string | null;
  /** Rendered HTML, ready to drop into `{@html}`. */
  html: string;
}

export function renderMarkdown(slug: string): ParsedMarkdown | null {
  const source = SOURCES[slug];
  if (!source) return null;

  // Pull the first H1 as the page title and strip it from the body so
  // MarkdownPage can hoist it into the PageShell title slot. Falls
  // through to null title if the file leads with anything else.
  const titleMatch = source.match(/^#\s+(.+?)\s*$/m);
  const title = titleMatch ? titleMatch[1].trim() : null;
  const body = titleMatch ? source.replace(titleMatch[0], '').trimStart() : source;

  const html = marked.parse(body, MARKED_OPTIONS) as string;
  return { title, html };
}
